import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { TopNav } from "@/components/TopNav";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Tv, Check, Swords, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";

export default function TVCheckin() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const { user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("idle"); // idle|checking|done|error
  const [checkins, setCheckins] = useState([]);
  const [error, setError] = useState("");

  const doCheckin = async () => {
    if (!token) { setError("QR không hợp lệ"); setStatus("error"); return; }
    setStatus("checking");
    try {
      await api.post("/tv/checkin", { token });
      setStatus("done");
      const { data } = await api.get("/tv/checkins");
      setCheckins(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Check-in thất bại");
      setStatus("error");
    }
  };

  useEffect(() => {
    if (user) doCheckin();
    // eslint-disable-next-line
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#05050a] grid place-items-center px-6">
        <div className="max-w-md w-full rounded-2xl bg-[#0c0d14] border border-white/8 p-6 text-center">
          <Tv size={36} className="text-cyan-300 mx-auto mb-3" />
          <h1 className="font-display font-bold text-xl mb-2">Đăng nhập để check-in</h1>
          <p className="text-sm text-white/60 mb-5">Bạn cần đăng nhập để vào sảnh chờ và hiển thị tên trên TV.</p>
          <Link to={`/login?next=/tv/checkin?t=${token}`} className="block px-5 py-3 rounded-md bg-amber-400/90 text-black font-display font-bold">Đăng nhập</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05050a]">
      <TopNav />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        {/* status card */}
        {status === "checking" && (
          <div className="rounded-2xl bg-[#0c0d14] border border-cyan-400/30 p-6 text-center" data-testid="checkin-pending">
            <div className="text-cyan-300 text-sm">Đang check-in...</div>
          </div>
        )}
        {status === "done" && (
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-6 text-center" data-testid="checkin-success">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 grid place-items-center mx-auto mb-3">
              <Check size={26} className="text-emerald-300" />
            </div>
            <h1 className="font-display font-bold text-2xl mb-1">Đã vào sảnh!</h1>
            <p className="text-sm text-white/70 mb-1">Tên & avatar của bạn đang hiển thị trên TV quán.</p>
            <p className="text-xs text-white/40">Hiệu lực 10 phút</p>
          </div>
        )}
        {status === "error" && (
          <div className="rounded-2xl border border-red-500/40 bg-red-500/5 p-6 text-center" data-testid="checkin-error">
            <ShieldCheck size={36} className="text-red-300 mx-auto mb-3" />
            <h1 className="font-display font-bold text-lg mb-1">Không check-in được</h1>
            <p className="text-sm text-red-300/80">{error}</p>
            <button onClick={doCheckin} className="mt-4 px-4 py-2 rounded-md text-sm bg-white/5 border border-white/10 hover:bg-white/10">Thử lại</button>
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => nav("/arena")} data-testid="checkin-go-arena" className="rounded-2xl bg-gradient-to-br from-red-500/15 to-rose-700/5 border border-red-500/30 p-4 text-left hover:border-red-500/60 transition">
            <Swords size={18} className="text-red-300 mb-2" />
            <div className="font-display font-bold text-sm mb-1">Tìm đối thủ</div>
            <div className="text-[11px] text-white/60">Vào đấu trường ghép trận ngay</div>
          </button>
          <button onClick={() => nav("/")} data-testid="checkin-go-home" className="rounded-2xl bg-gradient-to-br from-cyan-500/15 to-blue-700/5 border border-cyan-500/30 p-4 text-left hover:border-cyan-500/60 transition">
            <Users size={18} className="text-cyan-300 mb-2" />
            <div className="font-display font-bold text-sm mb-1">Về trang chủ</div>
            <div className="text-[11px] text-white/60">Xem ví, hồ sơ, leaderboard</div>
          </button>
        </div>

        {/* Lobby list */}
        {status === "done" && (
          <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-amber-300" />
                <h2 className="font-display font-bold text-sm uppercase tracking-wider">Người trong sảnh</h2>
              </div>
              <span className="text-xs text-white/40">{checkins.length}</span>
            </div>
            {checkins.length === 0 ? (
              <div className="text-white/40 text-sm text-center py-6">Bạn đang là người đầu tiên</div>
            ) : (
              <div className="space-y-2" data-testid="lobby-list">
                {checkins.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-md bg-white/[0.03] border border-white/5">
                    <img src={c.avatar_url} alt="" className="w-9 h-9 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate flex items-center gap-2">
                        {c.nickname}
                        {c.title_text && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 border border-amber-400/30">{c.title_text}</span>}
                      </div>
                      <div className="text-[10px] text-white/50">{c.rank} · {c.elo} ELO</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
